import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";

// --- Animations ---

const scanline = keyframes`
  0% { bottom: 100%; }
  100% { bottom: 0%; }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

// --- Styled Components ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 700px;
  min-height: 500px;
  padding: 2rem;
  box-sizing: border-box;
  background: linear-gradient(135deg, rgba(15, 15, 15, 0.9), rgba(30, 30, 30, 0.8));
  backdrop-filter: blur(10px);
  color: #e0e0e0;
  border: 1px solid #41ff46;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 1200px;
  margin: 1.2em auto;
  box-shadow: 0 0 15px rgba(65, 255, 70, 0.2);

  /* Force global font for all nested elements */
  font-family: "VT323", monospace !important;

  &:after {
    content: "";
    position: absolute;
    width: 100%;
    height: 2px;
    background: rgba(65, 255, 70, 0.1);
    animation: ${scanline} 6s linear infinite;
    pointer-events: none;
    z-index: 2;
  }

  @media (max-width: 1200px) {
    max-height: 90vh;
    min-height: 70vh;
    padding: 1rem 1rem 1rem 0.5rem;
    width: 93%;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 1rem;
  font-family: "VT323", monospace;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: rgba(0, 0, 0, 0.2); }
  &::-webkit-scrollbar-thumb { background: #41ff46; border-radius: 10px; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: rgba(20, 20, 20, 0.6);
  font-family: "VT323", monospace;
  table-layout: auto;

  @media (max-width: 650px) {
    display: block;
  }
`;

const Th = styled.th`
  font-family: "VT323", monospace;
  background-color: rgba(65, 255, 70, 0.1);
  color: #41ff46;
  padding: 1rem;
  text-align: left;
  border-bottom: 2px solid #41ff46;
  text-transform: uppercase;
  letter-spacing: 2px;
  white-space: nowrap;
  font-size: 1.4rem;

  @media (max-width: 650px) {
    display: none;
  }
`;

const Tr = styled.tr`
  border-bottom: 1px solid rgba(65, 255, 70, 0.1);
  font-family: "VT323", monospace;

  @media (max-width: 650px) {
    display: flex;
    flex-direction: column;
    padding: 1.5rem 0.5rem;
    border-bottom: 2px solid rgba(65, 255, 70, 0.3);
  }
`;

const Td = styled.td`
  font-family: "VT323", monospace;
  padding: 1rem;
  font-size: 1.5rem;
  color: #e0e0e0;
  vertical-align: middle;

  @media (max-width: 650px) {
    padding: 0.2rem 0;
    display: flex;
    flex-direction: column;

    &:before {
      content: attr(data-label);
      font-family: "VT323", monospace;
      color: #41ff46;
      font-size: 0.9rem;
      text-transform: uppercase;
      opacity: 0.8;
      margin-bottom: 0.2rem;
    }
  }
`;

const Button = styled.button`
  font-family: "VT323", monospace !important;
  background-color: transparent;
  border: 1px solid #44fd47;
  color: #44fd47;
  padding: 0.5vh 1.5vw;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: #44fd47;
    color: #000;
    box-shadow: 0 0 10px #44fd47;
  }

  &:disabled {
    opacity: 0.5;
    cursor: wait;
    animation: ${blink} 1s infinite;
  }

  @media (max-width: 650px) {
    width: 100%;
    margin-top: 1rem;
    padding: 1rem;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  z-index: 1000;
  backdrop-filter: blur(8px);
  font-family: "VT323", monospace;
`;

const ModalContent = styled.div`
  background-color: #1a1a1a;
  border: 2px solid ${(props) => props.color || "#41ff46"};
  color: #e0e0e0;
  padding: 2.5rem 2rem 2rem 2rem; /* Increased top padding for the X */
  border-radius: 8px;
  width: 100%;
  max-width: 600px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.5), 0 0 10px ${(props) => props.color || "#41ff46"};
  position: relative;
  font-family: "VT323", monospace !important;

  & * {
    font-family: "VT323", monospace !important;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 15px;
  background: none;
  border: none;
  color: ${(props) => props.color || "#41ff46"};
  font-family: "VT323", monospace !important;
  font-size: 2rem;
  cursor: pointer;
  line-height: 1;
  padding: 5px;
  transition: transform 0.2s ease, text-shadow 0.2s ease;
  z-index: 10;

  &:hover {
    transform: scale(1.2);
    text-shadow: 0 0 8px ${(props) => props.color || "#41ff46"};
  }
`;

const ModalTitle = styled.h2`
  font-family: "VT323", monospace !important;
  text-transform: uppercase;
  color: ${(props) => props.color || "#41ff46"};
  margin-bottom: 1.5rem;
  text-align: center;
  font-size: 2.8rem;
  letter-spacing: 3px;
`;

const SectionTitle = styled.h3`
  font-family: "VT323", monospace !important;
  color: #ffffff;
  margin-bottom: 1rem;
  font-size: 1.8rem;
  text-transform: uppercase;
`;

const ListItem = styled.li`
  font-family: "VT323", monospace !important;
  margin-bottom: 0.8rem;
  position: relative;
  padding-left: 1.5rem;
  font-size: 1.5rem;
  &:before {
    content: ">";
    color: ${(props) => props.color || "#44fd47"};
    position: absolute;
    left: 0;
    font-weight: bold;
    font-family: "VT323", monospace;
  }
`;

const InfoItem = styled.p`
  font-family: "VT323", monospace !important;
  margin-bottom: 0.8rem;
  font-size: 1.5rem;
  line-height: 1.4;

  & strong {
    font-family: "VT323", monospace !important;
  }
`;

// --- Main Component ---

const ArchetypeUtilizationChecklist = () => {
  const [archetypes, setArchetypes] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedArchetype, setSelectedArchetype] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => { fetchArchetypes(); }, []);

  useEffect(() => {
    if (modalVisible && containerRef.current) {
      const container = containerRef.current;
      const getScrollParent = (el) => {
        if (!el) return document.documentElement;
        return el.scrollHeight > el.clientHeight ? el : getScrollParent(el.parentElement);
      };
      const scrollParent = getScrollParent(container);
      const rect = container.getBoundingClientRect();
      const pos = scrollParent.scrollTop + (rect.top + rect.height / 2 - scrollParent.clientHeight * 0.6);
      scrollParent.scrollTo({ top: pos, behavior: "smooth" });
    }
  }, [modalVisible]);

  const fetchArchetypes = async () => {
    try {
      const response = await fetch("https://us-central1-archetype-builder-api.cloudfunctions.net/api/archetypes");
      const data = await response.json();
      setArchetypes(data); // Removed sort since order is string
    } catch (e) { console.error(e); }
  };

  const fetchArchetypeDetails = (name, id) => {
    const archetype = archetypes.find(a => a.id === id);
    if (archetype) {
      setSelectedArchetype({
        ...archetype,
        mythology: archetype.mythologicalReferences
      });
      setModalVisible(true);
    }
  };

  return (
    <Container ref={containerRef}>
      <ContentWrapper>
        <Table>
          <thead>
            <tr>
              <Th style={{ width: "60px" }}>ID</Th>
              <Th>Archetype</Th>
              <Th>Motto</Th>
              <Th style={{ width: "100px", textAlign: "center" }}>CMD</Th>
            </tr>
          </thead>
          <tbody>
            {archetypes.map((a) => (
              <Tr key={a.id}>
                <Td data-label="ID">{a.order}</Td>
                <Td data-label="Archetype" style={{ color: "#ffffff", fontWeight: "bold" }}>{a.name}</Td>
                <Td data-label="Motto">{a.motto}</Td>
                <Td>
                  <Button onClick={() => fetchArchetypeDetails(a.name, a.id)}>
                    EXEC
                  </Button>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </ContentWrapper>

      {modalVisible && selectedArchetype && (
        <ModalOverlay onClick={() => setModalVisible(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()} color={selectedArchetype.color}>
            <CloseButton color={selectedArchetype.color} onClick={() => setModalVisible(false)}>
              [X]
            </CloseButton>
            <ModalTitle color={selectedArchetype.color}>{selectedArchetype.name}</ModalTitle>
            <InfoItem><strong>Motto:</strong> {selectedArchetype.motto}</InfoItem>
            <InfoItem><strong>Planet:</strong> {selectedArchetype.planet}</InfoItem>

            <SectionTitle>Mission</SectionTitle>
            <InfoItem>{selectedArchetype.mission}</InfoItem>

            <SectionTitle>Idealized Form</SectionTitle>
            <InfoItem>{selectedArchetype.idealizedForm?.description}</InfoItem>

            <SectionTitle>Personality Scores</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {Object.entries(selectedArchetype.scores || {}).map(([key, value]) => (
                <ListItem key={key} color={selectedArchetype.color}>
                  {key}: {value}
                </ListItem>
              ))}
            </ul>

            <SectionTitle>Mythology</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {selectedArchetype.mythology?.map((m, i) => (
                <ListItem key={i} color={selectedArchetype.color}>
                  <strong>{m.name}:</strong> {m.description}
                </ListItem>
              ))}
            </ul>

            <SectionTitle>Historical Examples</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {selectedArchetype.historicalExamples?.map((m, i) => (
                <ListItem key={i} color={selectedArchetype.color}>
                  <strong>{m.name}:</strong> {m.description}
                </ListItem>
              ))}
            </ul>

            <SectionTitle>Traits</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {selectedArchetype.traits?.map((t, i) => (
                <ListItem key={i} color={selectedArchetype.color}>{t}</ListItem>
              ))}
            </ul>

            <SectionTitle>Motivations</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {selectedArchetype.motivations?.map((t, i) => (
                <ListItem key={i} color={selectedArchetype.color}>{t}</ListItem>
              ))}
            </ul>

            <SectionTitle>Behaviors</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {selectedArchetype.behaviors?.map((t, i) => (
                <ListItem key={i} color={selectedArchetype.color}>{t}</ListItem>
              ))}
            </ul>

            <SectionTitle>Interests</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {selectedArchetype.interests?.map((t, i) => (
                <ListItem key={i} color={selectedArchetype.color}>{t}</ListItem>
              ))}
            </ul>

            <SectionTitle>Practical Applications</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {selectedArchetype.practicalApplications?.map((t, i) => (
                <ListItem key={i} color={selectedArchetype.color}>{t}</ListItem>
              ))}
            </ul>

            <SectionTitle>Characteristics</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {Object.entries(selectedArchetype.characteristics || {}).map(([key, value]) => (
                <ListItem key={key} color={selectedArchetype.color}>
                  {key}: {value}
                </ListItem>
              ))}
            </ul>

            <SectionTitle>Ratings</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {Object.entries(selectedArchetype.ratings || {}).map(([key, value]) => (
                <ListItem key={key} color={selectedArchetype.color}>
                  {key}: {value}
                </ListItem>
              ))}
            </ul>

            <SectionTitle>Weaknesses</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {selectedArchetype.weaknesses?.map((t, i) => (
                <ListItem key={i} color={selectedArchetype.color}>{t}</ListItem>
              ))}
            </ul>

            <SectionTitle>Fears</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {selectedArchetype.fears?.map((t, i) => (
                <ListItem key={i} color={selectedArchetype.color}>{t}</ListItem>
              ))}
            </ul>

            <SectionTitle>Decision Making Style</SectionTitle>
            <InfoItem>{selectedArchetype.decisionMakingStyle}</InfoItem>

            <SectionTitle>Core Values</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {selectedArchetype.coreValues?.map((t, i) => (
                <ListItem key={i} color={selectedArchetype.color}>{t}</ListItem>
              ))}
            </ul>

            <SectionTitle>Physical and Emotional Appearance</SectionTitle>
            <InfoItem>{selectedArchetype.physicalAndEmotionalAppearance}</InfoItem>

            <SectionTitle>Symbolism and Iconography</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {selectedArchetype.symbolismAndIconography?.map((t, i) => (
                <ListItem key={i} color={selectedArchetype.color}>{t}</ListItem>
              ))}
            </ul>

            <SectionTitle>Preferred Environments</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {selectedArchetype.preferredEnvironments?.map((t, i) => (
                <ListItem key={i} color={selectedArchetype.color}>{t}</ListItem>
              ))}
            </ul>

            <SectionTitle>Core Skills and Abilities</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {selectedArchetype.coreSkillsAbilities?.map((t, i) => (
                <ListItem key={i} color={selectedArchetype.color}>{t}</ListItem>
              ))}
            </ul>

            <SectionTitle>Emotional Triggers</SectionTitle>
            <SectionTitle style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>Positive</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {selectedArchetype.emotionalTriggers?.positive?.map((t, i) => (
                <ListItem key={i} color={selectedArchetype.color}>{t}</ListItem>
              ))}
            </ul>
            <SectionTitle style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>Negative</SectionTitle>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {selectedArchetype.emotionalTriggers?.negative?.map((t, i) => (
                <ListItem key={i} color={selectedArchetype.color}>{t}</ListItem>
              ))}
            </ul>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ArchetypeUtilizationChecklist;